import { computed, ref, type InjectionKey } from 'vue'
import store from '@/store'
import {
  createVksRequest,
  getVksRequestStatus,
  connectVksSession,
  cancelVksRequest,
  sendVksDisconnect
} from '@/modules/VKS/services/vks'
import type { VksStatus } from '@/modules/VKS/services/types'

interface StartStudentVksParams {
  courseId: number | null
  flowId: number | null
  stageId: number | null
  questionId: number
}

interface StudentVksControllerOptions {
  onSessionDone?: () => void | Promise<void>
}

type StudentVksStatus = 'idle' | VksStatus

const TERMINAL_STATUSES: VksStatus[] = ['done', 'cancelled', 'expired', 'failed']
const EXAMINER_WAITING_TIMEOUT_MS = 5 * 60 * 60 * 1000

const StudentVksController = (options: StudentVksControllerOptions = {}) => {
  const status = ref<StudentVksStatus>('idle')
  const iframeUrl = ref<string | null>(null)
  const requestId = ref<number | null>(null)
  const activeQuestionId = ref<number | null>(null)
  const queuePosition = ref<number | null>(null)
  const waitingSeconds = ref(0)
  const isConnected = ref(false)
  const studentConnected = ref(false)
  const examinerConnected = ref(false)
  const isReconnecting = ref(false)
  const isCreating = ref(false)
  const isPolling = ref(false)

  let interval: ReturnType<typeof setInterval> | null = null
  let timeout: ReturnType<typeof setTimeout> | null = null

  const isIdle = computed(() => status.value === 'idle')
  const isWaiting = computed(() => status.value === 'waiting')
  const isActive = computed(
    () => !!iframeUrl.value && (status.value === 'reserved' || status.value === 'in_progress')
  )
  const isBlockingNavigation = computed(() => isActive.value)
  const isTerminal = computed(
    () => status.value !== 'idle' && TERMINAL_STATUSES.includes(status.value as VksStatus)
  )

  const stopPolling = () => {
    if (!interval) return
    clearInterval(interval)
    interval = null
  }

  const clearTimeoutSafe = () => {
    if (!timeout) return
    clearTimeout(timeout)
    timeout = null
  }

  const reset = () => {
    stopPolling()
    clearTimeoutSafe()

    status.value = 'idle'
    iframeUrl.value = null
    requestId.value = null
    activeQuestionId.value = null
    queuePosition.value = null
    waitingSeconds.value = 0
    isConnected.value = false
    studentConnected.value = false
    examinerConnected.value = false
    isReconnecting.value = false
    isPolling.value = false
  }

  const poll = async () => {
    if (isPolling.value) return
    if (!requestId.value) return

    try {
      isPolling.value = true

      const res = await getVksRequestStatus(requestId.value)
      const nextStatus = res.data?.status
      const joinUrl = res.data?.joinUrl ?? null

      queuePosition.value = res.data?.queue_position ?? null
      waitingSeconds.value = res.data?.waiting_seconds ?? 0
      studentConnected.value = res.data?.student_connected ?? false
      examinerConnected.value = res.data?.examiner_connected ?? false
      isReconnecting.value = res.data?.is_reconnecting ?? false

      if (!nextStatus) return

      status.value = nextStatus
      if (isReconnecting.value && !studentConnected.value) {
        isConnected.value = false
      }

      if ((nextStatus === 'reserved' || nextStatus === 'in_progress') && joinUrl) {
        clearTimeoutSafe()

        if (!isConnected.value) {
          await connectVksSession({
            request_id: requestId.value,
            role: 'student'
          })
          isConnected.value = true
          studentConnected.value = true
        }

        if (!iframeUrl.value) {
          iframeUrl.value = joinUrl
        }
      }

      if (TERMINAL_STATUSES.includes(nextStatus)) {
        stopPolling()
        clearTimeoutSafe()
        iframeUrl.value = null
        isConnected.value = false

        if (nextStatus === 'done') {
          await options.onSessionDone?.()
        }
      }
    } catch (e) {
      console.error('student vks polling error', e)

      stopPolling()
      clearTimeoutSafe()
      status.value = 'failed'
      iframeUrl.value = null
      isConnected.value = false

      store.ui.notices.newError('Ошибка соединения', 'Проблема при получении статуса видеовызова', {
        duration: 5000
      })
    } finally {
      isPolling.value = false
    }
  }

  const startPolling = () => {
    if (interval) return

    interval = setInterval(() => {
      poll()
    }, 3000)
  }

  const start = async ({ courseId, flowId, stageId, questionId }: StartStudentVksParams) => {
    if (isCreating.value || status.value !== 'idle') return

    if (!courseId || !flowId || !stageId) {
      store.ui.notices.newError(
        'Недостаточно данных для осуществления видеовызова',
        `courseId:${courseId} flowId:${flowId} stageId:${stageId}`,
        { duration: 5000 }
      )
      return
    }

    try {
      isCreating.value = true
      activeQuestionId.value = questionId

      const response = await createVksRequest({
        course: courseId,
        flow: flowId,
        stage: stageId,
        question: questionId
      })

      const createdRequestId = response.data?.request_id
      if (!createdRequestId) throw new Error('request_id не получен с сервера')

      requestId.value = createdRequestId
      status.value = response.data?.status ?? 'waiting'

      startPolling()
      poll()

      timeout = setTimeout(async () => {
        if (status.value !== 'waiting') return

        if (requestId.value) {
          await cancelVksRequest({
            request_id: requestId.value,
            reason: 'expired'
          })
        }

        reset()
        store.ui.notices.newWarning('Экзаменатор не ответил', 'Попробуйте позже', {
          duration: 5000
        })
      }, EXAMINER_WAITING_TIMEOUT_MS)
    } catch (e) {
      console.error('createVksRequest error', e)

      reset()
      store.ui.notices.newError('Ошибка соединения', 'Не удалось создать видеовызов', {
        duration: 5000
      })
    } finally {
      isCreating.value = false
    }
  }

  const clearTerminalStatus = () => {
    if (!isTerminal.value) return
    reset()
  }

  const disconnect = () => {
    if (!requestId.value || !isConnected.value) return

    sendVksDisconnect({
      request_id: requestId.value,
      ended_by: 'student'
    })
    isConnected.value = false
    studentConnected.value = false
  }

  const destroy = () => {
    disconnect()
    stopPolling()
    clearTimeoutSafe()
  }

  return {
    status,
    iframeUrl,
    requestId,
    activeQuestionId,
    queuePosition,
    waitingSeconds,
    studentConnected,
    examinerConnected,
    isReconnecting,
    isCreating,
    isIdle,
    isWaiting,
    isActive,
    isBlockingNavigation,
    isTerminal,
    start,
    clearTerminalStatus,
    disconnect,
    destroy
  }
}

export type StudentVksControllerInstance = ReturnType<typeof StudentVksController>
export const ProvidedStudentVksController = Symbol() as InjectionKey<StudentVksControllerInstance>

export default StudentVksController
